﻿import React from "react";
import { useAppSelector } from "../../../store";
import { useAppActions } from "../../../shared/hooks";
import { default as BaseDateTimePicker, type DateTimePickerProps } from "../../../shared/components/DateTimePicker";
import { PickerChangeHandlerContext, DateValidationError } from "@mui/x-date-pickers-pro";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { toDateFnsFormat } from "../../../shared/utilities";
import { toDate } from "date-fns";
import _ from "lodash";

type Props = DateTimePickerProps & {
    name: string;
    format?: string;
    minDateTime?: string | Date;
    maxDateTime?: string | Date;
    onChange?: (value: Date, originalValue: Date) => void | false | Date;
    isUTC?: boolean;
    referenceDate?: string | Date;
}

function DateTimePicker(props: Props) {
    const { name, format, onChange, minDateTime: _minDateTime, maxDateTime: _maxDateTime, referenceDate: _referenceDate, isUTC = false, ...other } = props;
    const parameterValues = useAppSelector(state => state.storyline.parameterValues);
    const { updateParameterValue } = useAppActions();
    const [value, setValue] = React.useState(parameterValues.get(name) as Date ?? null);
    const valueRef = React.useRef(value);
    const [open, setOpen] = React.useState(false);
    const [originalValue, setOriginalValue] = React.useState(value);
    const parameterValue = name ? parameterValues?.get(name) as Date : undefined;

    const persistSelectedValue = (newValue: Date, originalValue: Date) => {
        const valueToPersist = onChange?.(newValue, originalValue) ?? newValue;

        // User explicitly cancelled the update, short-circuit here...
        if (valueToPersist === false) return;

        updateParameterValue(name, valueToPersist);
    };

    const handleChange = (newValue: Date | null, context: PickerChangeHandlerContext<DateValidationError>) => {

        if (context.validationError && context.validationError == "invalidDate") {
            // clear value
            setValue(null);
            valueRef.current = null;
            console.warn("User-selected date is invalid, ignoring change due to:", context.validationError, "Received value was:", newValue);
            return;
        }

        if (isUTC) {
            newValue = newValue?.toUTCDateTime();
        }

        setValue(newValue);
        valueRef.current = newValue; // The handleClose event handler will read this value in order to an update + execute in the same render cycle.

        if (!open) {
            persistSelectedValue(newValue, originalValue);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setOriginalValue(value);
    }

    const handleClose = () => {
        setOpen(false);

        if (!_.isEqual(originalValue, valueRef.current)) {
            persistSelectedValue(valueRef.current, originalValue);
        }
    };

    React.useEffect(() => {
        // match value state to parameter value
        if (Boolean(name) && parameterValue !== value) {
            setValue(parameterValue);
        }
    }, [parameterValue, name]);

    // need to convert provided dates to UTC if isUTC is true
    const minDateTime = React.useMemo(() => {
        if (!_minDateTime) return undefined;

        return isUTC ? toDate(_minDateTime).toUTCDateTime() : toDate(_minDateTime);
    }, [_minDateTime, isUTC]);
    const maxDateTime = React.useMemo(() => {
        if (!_maxDateTime) return undefined;

        return isUTC ? toDate(_maxDateTime).toUTCDateTime() : toDate(_maxDateTime);
    }, [_maxDateTime, isUTC]);
    const referenceDate = React.useMemo(() => {
        if (!_referenceDate) return undefined;

        return isUTC ? toDate(_referenceDate).toUTCDateTime() : toDate(_referenceDate);
    }, [_referenceDate, isUTC]);

    return (
        <BaseDateTimePicker
            {...other}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            minDateTime={minDateTime}
            maxDateTime={maxDateTime}
            referenceDate={referenceDate}
            value={value}
            onChange={handleChange}
            format={format ? toDateFnsFormat(format) : "yyyy/MM/dd HH:mm"}
        />
    );
}

(DateTimePicker as DocumentedComponent).metadata = {
    description: `The \`DateTimePicker\` component allows the user to select a date (including time) value for a storyline parameter.  It does this via a multi-step calendar + clock control inside a pop-up.

_Use the \`DatePicker\` component if the \`time\` selection is not required._`,
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "The name of the parameter to update. The underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value." },
        { name: "format", type: "string", description: "The format of the date to display in the input box.  Defaults to 'YYYY/MM/DD HH:mm'." },
        { name: "minDateTime", type: "object", exactType: "string | Date", description: "The minimum datetime value that can be selected." },
        { name: "maxDateTime", type: "object", exactType: "string | Date", description: "The maximum datetime value that can be selected." },
        { name: "onChange", type: "function", exactType: "(newValue: Date, originalValue: string | Date) => void", template: "onChange={(value) => {$1}}", description: "An optional callback function that will be invoked when the selected date changes.  The updated value and the original value are passed in as arguments." },
        { name: "isUTC", type: "boolean", description: "If `true`, the selected date/time value will be treated as a UTC date/time (using UTCDate).  If `false`, it will be treated as a local date/time.  Defaults to `false`." },
        { name: "referenceDate", type: "object", exactType: "string | Date", description: "An optional date/time that is used as the reference point for the calendar display when no date/time has been selected yet. This will render the calendar view in the context of this date/time." }
    ]
};

export default DateTimePicker;